/** Waste & Voids module (QSR add-on) — waste/void by store & reason with AI. */
import React from 'react';
import { Trash2, Ban, DollarSign, AlertTriangle, ChevronRight, Sparkles } from 'lucide-react';
import KpiTile from './KpiTile';
import Heatmap from './Heatmap';
import { QSR_STORES, DAYPARTS, WASTE_DETAIL, avgBy, money } from './qsrDemoData';

const WASTE_SKEW = [1.0, 0.6, 0.9, 0.8, 1.3, 1.5, 1.9];

export default function WasteVoidsModule({ onSelectStore }) {
  const netWaste = avgBy('wastePct');
  const netVoid = avgBy('voidPct');
  const lost = QSR_STORES.reduce((s, x) => s + (WASTE_DETAIL[x.id]?.lostPerWk || 0), 0);
  const worst = [...QSR_STORES].sort((a, b) => b.wastePct - a.wastePct)[0];
  const heat = QSR_STORES.map((s) => WASTE_SKEW.map((p) => +(s.wastePct * p).toFixed(1)));

  // network reason mix (avg of stores)
  const rmap = {};
  QSR_STORES.forEach((x) => (WASTE_DETAIL[x.id]?.reasons || []).forEach((r) => { rmap[r.name] = (rmap[r.name] || 0) + r.pct; }));
  const reasons = Object.entries(rmap).map(([name, pct]) => ({ name, pct: Math.round(pct / QSR_STORES.length) })).sort((a, b) => b.pct - a.pct);
  const maxR = Math.max(...reasons.map((r) => r.pct));

  return (
    <div className="space-y-4 qsr-stage p-3 sm:p-5" data-testid="waste-module">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={Trash2} label="Network waste %" value={`${netWaste.toFixed(1)}%`} sub="target <2.5%" delta="-0.3%" deltaGood color="#f59e0b" trend={WASTE_DETAIL.footscray.wasteTrend} testid="waste-kpi-waste" ai={`Waste is ${netWaste.toFixed(1)}% vs a <2.5% target. Southport's remakes are the biggest driver — a prep-portion check could claw back most of it.`} />
        <KpiTile icon={Ban} label="Void %" source="Lightspeed POS" value={`${netVoid.toFixed(1)}%`} sub="of transactions" delta="-0.2%" deltaGood color="#ef4444" />
        <KpiTile icon={DollarSign} label="Est. margin lost" source="Lightspeed · modelled" value={`${money(lost)}/wk`} sub="3 stores" color="#8b5cf6" />
        <KpiTile icon={AlertTriangle} label="Worst store" source="Lightspeed" value={worst.name} sub={`${worst.wastePct.toFixed(1)}% waste`} color="#ef4444" />
      </div>

      <Heatmap
        rows={QSR_STORES.map((s) => s.name)}
        cols={DAYPARTS}
        values={heat}
        unit="%"
        invert
        title="Waste % by day-part"
        ai="Waste peaks at the late and close day-parts — end-of-night over-production and prep left unsold. A dynamic prep-down prompt in the last two windows is the quickest fix."
      />

      <div className="rounded-2xl qsr-card shadow-sm divide-y divide-slate-100">
        {[...QSR_STORES].sort((a, b) => b.wastePct - a.wastePct).map((s) => (
          <button key={s.id} onClick={() => onSelectStore?.(s)} className="w-full text-left flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors group" data-testid={`waste-row-${s.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-slate-900">{s.name}</div>
              <div className="flex gap-3 mt-1 text-[11px] text-slate-500 flex-wrap">
                <span>Waste <b className={s.wastePct > 2.5 ? 'text-rose-600' : 'text-emerald-600'}>{s.wastePct.toFixed(1)}%</b></span>
                <span>Voids <b className="text-slate-800">{s.voidPct.toFixed(1)}%</b></span>
                <span>Lost <b className="text-slate-800">{money(WASTE_DETAIL[s.id]?.lostPerWk || 0)}/wk</b></span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
          </button>
        ))}
      </div>

      <div className="rounded-2xl qsr-card p-4 shadow-sm">
        <div className="text-sm font-semibold text-slate-900 mb-3">Top waste reasons (network)</div>
        <div className="space-y-2.5">
          {reasons.map((r) => (
            <div key={r.name} className="flex items-center gap-3">
              <span className="w-44 text-xs text-slate-700 truncate">{r.name}</span>
              <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-rose-500 transition-[width] duration-700" style={{ width: `${(r.pct / maxR) * 100}%` }} />
              </div>
              <span className="w-9 text-right text-xs font-semibold text-slate-800">{r.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
        <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          <span className="font-semibold text-slate-900">Corvana AI:</span> Waste is costing the network ~{money(lost)}/wk.
          {' '}{worst.name} alone is {money(WASTE_DETAIL[worst.id]?.lostPerWk || 0)}/wk, driven by dropped/remake items at the
          dinner peak. A remake-cause prompt on the Lightspeed KDS plus a portioning refresher is the fastest margin win.
        </p>
      </div>
    </div>
  );
}
